import React, { useState, useEffect, useCallback } from 'react';
import { Menu, Image, Button, Dropdown, Icon, Modal } from 'semantic-ui-react'; 
import { useNavigate } from 'react-router-dom';
import '@styles/BlogHeader.css';
import '@styles/customColors.css';
import { menuData } from '@data/menuData';
import WebToLead from '@components/WebToLead';

const BlogHeader = ({ selectedTopLevelItem, setSelectedTopLevelItem }) => {
  const [activeItem, setActiveItem] = useState('home');
  const [deviceType, setDeviceType] = useState(getDeviceType());
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();

  // Function to determine the device type based on window width
  function getDeviceType() {
    const width = window.innerWidth;
    if (width <= 768) return 'mobile';
    if (width <= 991) return 'tablet';
    return 'desktop';
  }

  // Handler for menu item clicks
  const handleItemClick = (e, { name }) => {
    setActiveItem(name);
    setSelectedTopLevelItem(name);
    navigate(`/${name.toLowerCase()}`);
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  // Debounce function to limit the rate at which a function can fire.
  const debounce = (func, delay) => {
    let timer;
    return () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func();
      }, delay);
    };
  };

  // Function to update the device type based on window width
  const updateDeviceType = useCallback(() => {
    const currentDeviceType = getDeviceType();
    setDeviceType(currentDeviceType);
  }, []);

  // Set up event listener for window resize with debounce
  useEffect(() => {
    const debouncedResizeHandler = debounce(updateDeviceType, 150);
    window.addEventListener('resize', debouncedResizeHandler);
    return () => window.removeEventListener('resize', debouncedResizeHandler);
  }, [updateDeviceType]);

  // Function to render menu items as Buttons (for Desktop)
  const renderMenuButtons = (items) => {
    const menuButtons = items.map((item, index) => (
      <Menu.Item
        key={index}
        name={item.title}
        active={activeItem === item.title}
        onClick={handleItemClick}
      >
        <Button className={`custom-button ${activeItem === item.title ? 'active' : ''}`}>
          {item.title}
        </Button>
      </Menu.Item>
    ));
    return [
      ...menuButtons,
      <Menu.Item key="contact">
        <Button className="custom-button" onClick={handleOpenModal}>
          Get In Touch
        </Button>
      </Menu.Item>
    ];
  };

  // Function to render menu items as Dropdown Items (for Mobile and Tablet)
  const renderDropdownItems = (items) => {
    const dropdownItems = items.map((item, index) => (
      <Dropdown.Item
        key={index}
        text={item.title}
        name={item.title}
        active={activeItem === item.title}
        onClick={handleItemClick}
      />
    ));
    return [
      ...dropdownItems,
      <Dropdown.Item key="contact" onClick={handleOpenModal}>
        Get In Touch
      </Dropdown.Item>
    ];
  };

  return (
    <div>
      <Menu 
        secondary 
        pointing 
        className="header-menu" 
        style={{ 
          display: 'flex !important', 
          alignItems: 'center !important', 
          minHeight: '80px',
          paddingTop: '0 !important',
          paddingBottom: '0 !important'
        }}
      >
        {/* Conditionally render the logo as a regular Menu.Item or as a Dropdown trigger */}
        {deviceType === 'desktop' ? (
          <Menu.Item className="logo-item" name="home" onClick={handleItemClick} style={{ display: 'flex', alignItems: 'center' }}>
            <Image
              src={`${process.env.PUBLIC_URL}/assets/CZRLogoCC.svg`}
              alt="Logo"
              className="header-logo"
            />
          </Menu.Item>
        ) : (
          <Menu.Item>
            <Dropdown
              trigger={
                <Icon name="bars" size="large" className="menu-icon" style={{ color: '#a9885c' }} />
              }
              pointing="left"
              icon={null}
              className="dropdown-menu"
            >
              <Dropdown.Menu>
                {menuData && menuData.menu && renderDropdownItems(menuData.menu)}
              </Dropdown.Menu>
            </Dropdown>
          </Menu.Item>
        )}

        {/* Render menu items as Buttons on Desktop */}
        {deviceType === 'desktop' && menuData && menuData.menu && renderMenuButtons(menuData.menu)}

        {/* Header Logo - Right aligned for desktop, centered and larger for mobile */}
        {deviceType === 'desktop' ? (
          <Menu.Item position="right" style={{ display: 'flex', alignItems: 'center' }}>
            <Image
              src={`${process.env.PUBLIC_URL}/assets/LogoSoPretty.svg`}
              alt="Cloud Caesar Logo"
              className="header-logo-text"
              style={{ height: '60px', width: 'auto' }}
            />
          </Menu.Item>
        ) : (
          <div className="header-text">Cloud Caesar</div>
        )}
      </Menu>

      {/* WebToLead Modal */}
      <Modal open={modalOpen} onClose={handleCloseModal} size='small' style={{ backgroundColor: '#702963' }}>
        <Modal.Header style={{ backgroundColor: '#702963', color: '#a9885c' }}>Contact Us</Modal.Header>
        <Modal.Content style={{ backgroundColor: '#a9885c' }}>
          <WebToLead />
        </Modal.Content>
        <Modal.Actions style={{ backgroundColor: '#702963', color: '#a9885c' }}>
          <Button style={{ backgroundColor: '#a9885c', color: '#702963' }} onClick={handleCloseModal}>Close</Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default BlogHeader;